import maplibregl, { type LngLatLike, type Map } from "maplibre-gl";
import type { TFunction } from "i18next";

export type PopupAnchor =
  | "center"
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

type LotPopupOptions = {
  map: Map;
  lngLat: LngLatLike;
  t: TFunction;
  lotId?: string | null;
  workspaceName?: string | null;
  title: string;
  area: number | null;
  workspaceId?: string | null;
  onViewDetails?: () => void;
  closeButton?: boolean;
  anchor?: PopupAnchor;
  onClose?: () => void;
};

const formatNumber = (value: number, fractionDigits = 2): string =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

const popupWrapperClasses =
  "lot-popup flex min-w-[14rem] max-w-[18rem] flex-col gap-2 text-sm text-slate-900 dark:text-slate-100";
const titleButtonClasses =
  "text-base font-semibold text-slate-900 underline-offset-4 transition hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:text-slate-50 dark:hover:text-blue-200 dark:focus:ring-offset-slate-900";
const titleSpanClasses = "text-base font-semibold text-slate-900 dark:text-slate-50";
const rowClasses = "flex items-center justify-between text-xs text-slate-600 dark:text-slate-300";
const labelClasses = "font-semibold text-slate-700 dark:text-slate-100";
const valueBadgeClasses =
  "rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-800 shadow-sm dark:bg-slate-800 dark:text-slate-100";
const actionButtonClasses = "pm-btn pm-btn-primary pm-btn-sm mt-2 w-full justify-center text-white";

export function openLotPopup({
  map,
  lngLat,
  t,
  lotId,
    title,
    area,
  workspaceName,
  workspaceId,
  onViewDetails,
  closeButton = false,
  anchor = "top",
  onClose,
}: LotPopupOptions): maplibregl.Popup {
  const titleLine =
    lotId && onViewDetails
      ? `<button class="${titleButtonClasses} lot-popup__title" type="button" data-lot-id="${lotId}">${title}</button>`
      : `<span class="${titleSpanClasses}">${title}</span>`;

  const rows = [
    workspaceName || workspaceId
      ? `<div class="${rowClasses}"><span class="${labelClasses}">${t("workspaces.lots.map.workspace_label", "Workspace")}</span><span class="${valueBadgeClasses}">${workspaceName ?? workspaceId}</span></div>`
      : null,
    area != null
      ? `<div class="${rowClasses}"><span class="${labelClasses}">${t("workspaces.lots.map.area_label", "Area")}</span><span class="${valueBadgeClasses}">${formatNumber(area)} m²</span></div>`
      : null,
  ].filter(Boolean);

  if (onViewDetails && lotId) {
    rows.push(
      `<button class="${actionButtonClasses} lot-popup__action" type="button" data-lot-id="${lotId}">${t("workspaces.lots.map.view_details")}</button>`,
    );
  }

  const popup = new maplibregl.Popup({
    closeButton,
    closeOnClick: true,
    anchor,
  })
    .setLngLat(lngLat)
    .setHTML(
      `
        <div class="${popupWrapperClasses}">
          ${titleLine}
          ${rows.map((row) => `<div>${row}</div>`).join("")}
        </div>
      `,
    )
    .addTo(map);

  const popupEl = popup.getElement();

  if (onViewDetails && lotId) {
    const actionButtons = popupEl.querySelectorAll<HTMLButtonElement>(
      ".lot-popup__title[data-lot-id], .lot-popup__action[data-lot-id]",
    );
    actionButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        onViewDetails();
        popup.remove();
      });
    });
  }

  if (onClose) {
    popup.on("close", onClose);
  }

  return popup;
}
